import React from "react";
import {
  DefaultSmallFallback,
  CriticalActionFallback,
  FormSectionFallback,
} from "@/components/utils/ErrorFallbacks";
import ErrorBoundary from "./errorBoundary";

export interface ErrorFallbackProps {
  error?: Error;
  resetError?: () => void;
  componentName?: string;
}

export type FallbackType = "small" | "critical" | "form" | "custom";

interface WithErrorBoundaryOptions {
  fallbackType?: FallbackType;
  componentName?: string;
  customFallback?: React.ComponentType<ErrorFallbackProps>;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

export function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  options: WithErrorBoundaryOptions = {}
) {
  const {
    fallbackType = "small",
    componentName,
    customFallback,
    onError,
  } = options;

  const displayName =
    componentName || Component.displayName || Component.name || "Component";

  const getFallbackComponent = () => {
    if (customFallback) return customFallback;

    switch (fallbackType) {
      case "critical":
        return CriticalActionFallback;
      case "form":
        return FormSectionFallback;
      case "small":
      default:
        return DefaultSmallFallback;
    }
  };

  const WrappedComponent: React.FC<P> = props => (
    <ErrorBoundary
      componentName={displayName}
      fallback={getFallbackComponent()}
      onError={onError}
    >
      <Component {...props} />
    </ErrorBoundary>
  );

  WrappedComponent.displayName = `withErrorBoundary(${displayName})`;

  return WrappedComponent;
}

interface InlineErrorBoundaryProps {
  children: React.ReactNode;
  fallbackType?: FallbackType;
  componentName?: string;
  customFallback?: React.ComponentType<ErrorFallbackProps>;
}

export const InlineErrorBoundary: React.FC<InlineErrorBoundaryProps> = ({
  children,
  fallbackType = "small",
  componentName = "Component",
  customFallback,
}) => {
  const getFallbackComponent = () => {
    if (customFallback) return customFallback;

    switch (fallbackType) {
      case "critical":
        return CriticalActionFallback;
      case "form":
        return FormSectionFallback;
      case "small":
      default:
        return DefaultSmallFallback;
    }
  };

  return (
    <ErrorBoundary
      componentName={componentName}
      fallback={getFallbackComponent()}
    >
      {children}
    </ErrorBoundary>
  );
};
